import { AgGridTable, AgGridTableRef } from '@shared/components/ui/AgGridTable';
import { ColDef, GridOptions } from 'ag-grid-community';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { isMinimumFeeInvoiceable, MinimumFee } from '../../models/MinimumFee';
import { useMinimumFeeColumns } from '../../hooks/useMinimumFeeColumns';
import { SelectionToolbar } from '../interaction/SelectionToolbar';

interface MinimumFeesAgGridTableProps {
  fees: MinimumFee[];
  selectedColumns?: string[];
  onViewInvoice: (invoiceId: string) => void;
  selectedFeeIds?: Set<string>;
  onClearSelection?: () => void;
  onSelectionChanged?: (selectedFeeIds: Set<string>) => void;
  onInvoiceSelected?: () => void;
  isInvoicing?: boolean;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}

export const MinimumFeesAgGridTable = React.memo(function MinimumFeesAgGridTable({
  fees,
  selectedColumns = [],
  onViewInvoice,
  selectedFeeIds,
  onClearSelection,
  onSelectionChanged,
  onInvoiceSelected,
  isInvoicing = false,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: MinimumFeesAgGridTableProps) {
  const agGridRef = useRef<AgGridTableRef>(null);
  const [localSelectedCount, setLocalSelectedCount] = useState(0);
  const selectionEnabled = Boolean(onSelectionChanged);

  const { filteredColumnDefs } = useMinimumFeeColumns({
    selectedColumns,
    onViewInvoice,
  });

  // fitGridWidth + columnPinning explícito: evita que `actions` quede al final del scroll en la carga inicial
  // (mismo fix que BoxGroupsTable; el autoSizeStrategy por defecto de AgGridTable usa fitCellContents)
  const gridOptions = useMemo<GridOptions>(
    () => ({
      autoSizeStrategy: {
        type: 'fitGridWidth',
        defaultMinWidth: 100,
      },
      initialState: {
        sort: { sortModel: [] },
        columnPinning: {
          leftColIds: [],
          rightColIds: ['actions'],
        },
        partialColumnState: true,
      },
      // Config de selección en el módulo (sin mutar AgGridTable en onGridReady)
      ...(selectionEnabled
        ? {
            rowSelection: {
              mode: 'multiRow',
              enableClickSelection: false,
              checkboxes: true,
              headerCheckbox: true,
              isRowSelectable: (rowNode) => isMinimumFeeInvoiceable(rowNode.data as MinimumFee | undefined),
              hideDisabledCheckboxes: true,
            },
          }
        : {}),
    }),
    [selectionEnabled],
  );

  const clearSelectionUI = useCallback(() => {
    agGridRef.current?.deselectAll();
    setLocalSelectedCount(0);
  }, []);

  const handleCloseSelectionToolbar = useCallback(() => {
    clearSelectionUI();
    onClearSelection?.();
  }, [clearSelectionUI, onClearSelection]);

  const handleSelectionChanged = useCallback(
    (selectedRows: MinimumFee[]) => {
      if (!selectionEnabled) {
        return;
      }

      const invoiceableRows = selectedRows.filter((fee) => isMinimumFeeInvoiceable(fee));
      const selectedIds = new Set(invoiceableRows.map((fee) => fee.id));
      setLocalSelectedCount(invoiceableRows.length);

      onSelectionChanged?.(selectedIds);
    },
    [onSelectionChanged, selectionEnabled],
  );

  React.useEffect(() => {
    if (!selectionEnabled) {
      if (localSelectedCount !== 0) {
        clearSelectionUI();
        onClearSelection?.();
      }
      return;
    }

    if (selectedFeeIds && selectedFeeIds.size === 0 && localSelectedCount !== 0) {
      clearSelectionUI();
    }
  }, [selectedFeeIds, clearSelectionUI, selectionEnabled, onClearSelection, localSelectedCount]);

  return (
    <div className="w-full">
      <AgGridTable
        ref={agGridRef}
        data={fees}
        columnDefs={filteredColumnDefs as ColDef[]}
        height="calc(100vh - 140px)"
        enableSelection={selectionEnabled}
        onSelectionChanged={handleSelectionChanged}
        onGridReady={() => undefined}
        className="rounded-lg shadow-sm"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        gridOptions={gridOptions}
        rowHeight={50}
        showLoadMoreButton={false}
      />

      {selectionEnabled && (
        <SelectionToolbar
          selectedCount={localSelectedCount || selectedFeeIds?.size || 0}
          onClose={handleCloseSelectionToolbar}
          onInvoice={onInvoiceSelected}
          isInvoicing={isInvoicing}
        />
      )}
    </div>
  );
});
